// src/components/cards/InfoSoldeCard.tsx
import React, { useEffect, useState } from 'react';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Link } from 'react-router-dom';

type Props = {
  date: string; // format YYYY-MM-DD
};

type SoldeBucket = {
  encours: number;
};

type ApiSolde = {
  date: string;
  year: number;
  month: number;
  solde: {
    journalier: SoldeBucket;
    mensuel: SoldeBucket;
    annuel: SoldeBucket;
    antecedent?: SoldeBucket; // optionnel suivant ton API
  };
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n ?? 0);

export default function InfoSoldeCard({ date }: Props) {
  const [data, setData] = useState<ApiSolde | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSolde = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get('/info-solde', { params: { date } });
        setData(res.data as ApiSolde);
      } catch (err: any) {
        setData(null);
        setError(err?.response?.data?.error || 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchSolde();
  }, [date]);

  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-5 pb-4 pt-5 dark:border-darkBorderCard dark:bg-white/[0.03] sm:px-6 sm:pt-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-xl bg-gray-100 text-gray-800 dark:bg-darkDarkGray dark:text-white/90">
            <BanknotesIcon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Encours clients
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Date de référence :{' '}
              <span className="text-red-500 dark:text-red-400">
                {date}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading && <div className="text-gray-500">Chargement…</div>}
      {!loading && data && (
        <div className="flex flex-col gap-4">
            {/* Journalier */}
            <div className="flex flex-col border-b border-gray-200 pb-3 last:border-0">
            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Journalier</p>
            <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(data.solde.journalier?.encours ?? 0)}
            </h4>
            <span className="text-xs text-gray-400 dark:text-gray-500">Encours du jour</span>
            </div>

            {/* Mensuel */}
            <div className="flex flex-col border-b border-gray-200 pb-3 last:border-0">
            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Mensuel</p>
            <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(data.solde.mensuel?.encours ?? 0)}
            </h4>
            <span className="text-xs text-gray-400 dark:text-gray-500">Encours du mois</span>
            </div>

            {/* Annuel */}
            <div className="flex flex-col border-b border-gray-200 pb-3 last:border-0">
            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Annuel</p>
            <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(data.solde.annuel?.encours ?? 0)}
            </h4>
            <span className="text-xs text-gray-400 dark:text-gray-500">Encours de l’année</span>
            </div>

            {/* Antécédents */}
            <div className="flex flex-col">
            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Précendent l'année actuelle</p>
            <h4 className="mb-1 text-2xl font-semibold text-gray-800 dark:text-white/90">
                {fmtMoney(data.solde.antecedent?.encours ?? 0)}
            </h4>
            <span className="text-xs text-gray-400 dark:text-gray-500">Années précédentes</span>
            </div>

            {/* Bouton en bas */}
            <div className="mt-6  flex justify-center">
              <Link
                to="/factures?status=IMPAYEE"
                className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700"
              >
                Voir les factures impayées
              </Link>
            </div>
        </div>
        )}

    </div>
  );
}
